<template>
  <section class="ncrm-pbx">
    <div class="d-flex flex-wrap align-center ga-3 mb-4">
      <div>
        <h2 class="ncrm-pbx__title">{{ $t("ncrm.integrations.pbx.title") }}</h2>
        <p class="ncrm-pbx__subtitle">{{ $t("ncrm.integrations.pbx.subtitle") }}</p>
      </div>
      <v-spacer />
      <v-btn color="primary" variant="tonal" :loading="loading" @click="loadAll">
        <v-icon start>mdi-refresh</v-icon>
        {{ $t("app.actions.refresh") }}
      </v-btn>
    </div>

    <v-row>
      <v-col cols="12" lg="4">
        <v-card variant="flat" class="pa-4 ncrm-pbx__panel">
          <div class="d-flex align-center ga-2 mb-3">
            <v-icon color="primary">mdi-phone-settings-outline</v-icon>
            <strong>{{ $t("ncrm.integrations.pbx.account.title") }}</strong>
            <v-spacer />
            <v-chip v-if="selectedAccount" size="small" v-bind="statusMeta(selectedAccount.status)">
              {{ statusLabel(selectedAccount.status) }}
            </v-chip>
          </div>

          <v-alert v-if="formError" class="mb-3" type="error">{{ formError }}</v-alert>

          <v-form @submit.prevent="saveAccount">
            <v-text-field v-model="draft.name" density="compact" :label="$t('ncrm.integrations.pbx.fields.name')" />
            <v-select
              v-model="draft.apiMode"
              density="compact"
              :items="apiModeOptions"
              :label="$t('ncrm.integrations.pbx.fields.apiMode')"
            />
            <v-select
              v-model="draft.status"
              density="compact"
              :items="statusOptions"
              :label="$t('ncrm.integrations.pbx.fields.status')"
            />
            <v-text-field v-model="draft.baseUrl" density="compact" :label="$t('ncrm.integrations.pbx.fields.baseUrl')" />
            <v-text-field v-model="draft.pbxDomain" density="compact" :label="$t('ncrm.integrations.pbx.fields.pbxDomain')" />
            <v-text-field
              v-if="draft.apiMode === 'proxy'"
              v-model="draft.serialNumber"
              density="compact"
              :label="$t('ncrm.integrations.pbx.fields.serialNumber')"
            />
            <v-text-field v-model="draft.username" density="compact" :label="$t('ncrm.integrations.pbx.fields.username')" />
            <v-text-field
              v-model="draft.password"
              density="compact"
              type="password"
              :label="passwordLabel"
              :hint="selectedAccount ? $t('ncrm.integrations.pbx.fields.passwordHint') : ''"
              persistent-hint
            />
            <v-row dense>
              <v-col cols="7">
                <v-switch
                  v-model="draft.syncEnabled"
                  color="primary"
                  density="compact"
                  hide-details
                  :label="$t('ncrm.integrations.pbx.fields.syncEnabled')"
                />
              </v-col>
              <v-col cols="5">
                <v-text-field
                  v-model.number="draft.syncIntervalMinutes"
                  density="compact"
                  type="number"
                  min="1"
                  :label="$t('ncrm.integrations.pbx.fields.syncInterval')"
                />
              </v-col>
            </v-row>
            <div class="d-flex flex-wrap ga-2 mt-2">
              <v-btn color="primary" type="submit" :loading="saving">
                <v-icon start>mdi-content-save-outline</v-icon>
                {{ selectedAccount ? $t("app.actions.save") : $t("ncrm.integrations.pbx.actions.create") }}
              </v-btn>
              <v-btn v-if="selectedAccount" variant="tonal" color="primary" :loading="testing" @click="testSelectedAccount">
                <v-icon start>mdi-lan-check</v-icon>
                {{ $t("ncrm.integrations.pbx.actions.test") }}
              </v-btn>
            </div>
          </v-form>

          <v-divider class="my-4" />

          <div v-if="accounts.length" class="ncrm-pbx__accounts">
            <button
              v-for="account in accounts"
              :key="account.id"
              class="ncrm-pbx__account-row"
              :class="{ 'ncrm-pbx__account-row--active': account.id === selectedAccountId }"
              type="button"
              @click="selectAccount(account)"
            >
              <span>{{ account.name }}</span>
              <span class="d-inline-flex align-center ga-1">
                <v-chip
                  v-if="String(account.last_sync_status || '').toLowerCase() === 'error'"
                  size="x-small"
                  v-bind="statusMeta('error')"
                  :title="account.last_sync_error || ''"
                >
                  <v-icon start size="x-small">mdi-phone-sync-outline</v-icon>
                  {{ statusLabel("error") }}
                </v-chip>
                <v-chip size="x-small" v-bind="statusMeta(account.last_test_status)">
                  {{ statusLabel(account.last_test_status) }}
                </v-chip>
              </span>
            </button>
          </div>
          <v-alert v-else type="info">{{ $t("ncrm.integrations.pbx.account.empty") }}</v-alert>
        </v-card>
      </v-col>

      <v-col cols="12" lg="8">
        <div class="ncrm-pbx__main-stack">
          <NcrmPbxSyncPanel
            :account="selectedAccount"
            :loading="syncing"
            :result="lastSyncResult"
            :error="syncError"
            @sync="syncSelectedAccount"
            @basis-updated="handleBasisUpdated"
          />
          <NcrmPbxCallsTable
            :calls="calls"
            :loading="callsLoading"
            :status-filter="callStatusFilter"
            :downloading-id="downloadingCallId"
            @status-change="handleCallStatusChange"
            @raw="openRaw"
            @link="openLink"
            @open-entity="openMatchedEntity"
            @download="downloadRecording"
          />
        </div>
      </v-col>
    </v-row>

    <v-navigation-drawer v-model="rawDrawer.show" location="right" temporary width="520">
      <div class="pa-4">
        <div class="d-flex align-center mb-3">
          <strong>{{ $t("ncrm.integrations.pbx.raw.title") }}</strong>
          <v-spacer />
          <v-btn icon variant="text" @click="rawDrawer.show = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <pre class="ncrm-pbx__raw">{{ rawPayload }}</pre>
      </div>
    </v-navigation-drawer>

    <v-navigation-drawer v-model="linkDrawer.show" location="right" temporary width="420">
      <div class="pa-4">
        <div class="d-flex align-center mb-3">
          <strong>{{ $t("ncrm.integrations.pbx.link.title") }}</strong>
          <v-spacer />
          <v-btn icon variant="text" @click="linkDrawer.show = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-alert v-if="linkDrawer.error" class="mb-3" type="error">{{ linkDrawer.error }}</v-alert>
        <v-select v-model="linkDrawer.entityType" density="compact" :items="entityTypeOptions" :label="$t('ncrm.integrations.pbx.link.entityType')" />
        <v-text-field v-model.number="linkDrawer.entityId" density="compact" type="number" min="1" :label="$t('ncrm.integrations.pbx.link.entityId')" />
        <v-text-field v-model="linkDrawer.matchedPhone" density="compact" :label="$t('ncrm.integrations.pbx.link.phone')" />
        <div class="d-flex ga-2 mt-2">
          <v-btn color="primary" :loading="linkDrawer.saving" @click="submitLink">{{ $t("app.actions.save") }}</v-btn>
          <v-btn variant="text" @click="linkDrawer.show = false">{{ $t("app.actions.cancel") }}</v-btn>
        </div>
      </div>
    </v-navigation-drawer>

    <v-snackbar v-model="snackbar.show" :timeout="3500" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>
  </section>
</template>

<script>
import {
  buildPbxAccountPayload,
  createPbxAccount,
  createPbxDraft,
  createPbxDraftFromAccount,
  fetchPbxAccounts,
  fetchPbxCalls,
  isPbxRecordingUnavailableError,
  linkPbxCall,
  normalizePbxAccount,
  normalizePbxCall,
  resolvePbxStatusMeta,
  syncPbxAccount,
  testPbxAccount,
  triggerPbxRecordingDownload,
  updatePbxAccount,
} from "@/Utile/ncrmPbx.mjs";
import NcrmPbxCallsTable from "@/components/ncrm/integrations/NcrmPbxCallsTable.vue";
import NcrmPbxSyncPanel from "@/components/ncrm/integrations/NcrmPbxSyncPanel.vue";

export default {
  name: "NcrmPbxWorkspace",
  components: {
    NcrmPbxCallsTable,
    NcrmPbxSyncPanel,
  },
  data() {
    return {
      accounts: [],
      selectedAccountId: null,
      draft: createPbxDraft(),
      loading: false,
      saving: false,
      testing: false,
      syncing: false,
      formError: "",
      calls: [],
      callsLoading: false,
      callStatusFilter: "unmatched",
      downloadingCallId: null,
      lastSyncResult: null,
      syncError: "",
      rawDrawer: { show: false, call: null },
      linkDrawer: { show: false, call: null, entityType: "company", entityId: null, matchedPhone: "", saving: false, error: "" },
      snackbar: { show: false, message: "", color: "info" },
    };
  },
  computed: {
    selectedAccount() {
      return this.accounts.find((account) => account.id === this.selectedAccountId) || null;
    },
    passwordLabel() {
      return this.selectedAccount
        ? this.$t("ncrm.integrations.pbx.fields.passwordOptional")
        : this.$t("ncrm.integrations.pbx.fields.password");
    },
    apiModeOptions() {
      return [
        { title: this.$t("ncrm.integrations.pbx.apiMode.direct"), value: "direct" },
        { title: this.$t("ncrm.integrations.pbx.apiMode.proxy"), value: "proxy" },
      ];
    },
    statusOptions() {
      return ["draft", "active", "paused", "disabled"].map((value) => ({ title: this.statusLabel(value), value }));
    },
    entityTypeOptions() {
      return ["company", "person", "deal", "contract"].map((value) => ({ title: this.statusLabel(value), value }));
    },
    rawPayload() {
      return JSON.stringify(this.rawDrawer.call?.raw_payload_json || {}, null, 2);
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    statusMeta(status) {
      return resolvePbxStatusMeta(status);
    },
    statusLabel(value) {
      return this.$t(`ncrm.integrations.pbx.status.${String(value || "unknown").replace(/\s+/g, "_").toLowerCase()}`, String(value || "-"));
    },
    extractError(error, fallbackKey) {
      return error?.response?.data?.error || error?.message || this.$t(fallbackKey);
    },
    showSnackbar(message, color = "info") {
      this.snackbar = { show: true, message, color };
    },
    handleBasisUpdated(account) {
      if (!account) return;
      const updated = normalizePbxAccount(account);
      const index = this.accounts.findIndex((item) => item.id === updated.id);
      if (index >= 0) this.accounts.splice(index, 1, updated);
    },
    async loadAll() {
      this.loading = true;
      try {
        const { data } = await fetchPbxAccounts();
        this.accounts = Array.isArray(data?.accounts) ? data.accounts.map(normalizePbxAccount) : [];
        if (!this.selectedAccountId && this.accounts[0]) this.selectAccount(this.accounts[0]);
        await this.fetchCalls();
      } catch (error) {
        this.showSnackbar(this.extractError(error, "ncrm.integrations.pbx.snackbar.loadError"), "error");
      } finally {
        this.loading = false;
      }
    },
    selectAccount(account) {
      this.lastSyncResult = null;
      this.syncError = "";
      this.selectedAccountId = account?.id || null;
      this.draft = createPbxDraftFromAccount(account);
    },
    validateDraft() {
      const required = [this.draft.name, this.draft.baseUrl, this.draft.pbxDomain, this.draft.username];
      if (required.some((value) => !String(value || "").trim())) return false;
      if (!this.selectedAccount && !String(this.draft.password || "").trim()) return false;
      return true;
    },
    async saveAccount() {
      this.formError = "";
      if (!this.validateDraft()) {
        this.formError = this.$t("ncrm.integrations.pbx.snackbar.required");
        return;
      }
      this.saving = true;
      try {
        const payload = buildPbxAccountPayload(this.draft, { includePassword: Boolean(this.draft.password) || !this.selectedAccount });
        const { data } = this.selectedAccount
          ? await updatePbxAccount(this.selectedAccount.id, payload)
          : await createPbxAccount(payload);
        const saved = normalizePbxAccount(data.account);
        const index = this.accounts.findIndex((account) => account.id === saved.id);
        if (index >= 0) this.accounts.splice(index, 1, saved);
        else this.accounts.unshift(saved);
        this.selectAccount(saved);
        this.showSnackbar(this.$t("ncrm.integrations.pbx.snackbar.saved"), "success");
      } catch (error) {
        this.formError = this.extractError(error, "ncrm.integrations.pbx.snackbar.saveError");
      } finally {
        this.saving = false;
      }
    },
    async testSelectedAccount() {
      if (!this.selectedAccount) return;
      this.testing = true;
      try {
        const { data, status } = await testPbxAccount(this.selectedAccount.id);
        this.showSnackbar(data?.ok ? this.$t("ncrm.integrations.pbx.snackbar.testOk") : data?.error || this.$t("ncrm.integrations.pbx.snackbar.testError"), status < 300 ? "success" : "error");
        await this.loadAll();
      } finally {
        this.testing = false;
      }
    },
    async syncSelectedAccount(payload = {}) {
      if (!this.selectedAccount) return;
      this.syncing = true;
      this.syncError = "";
      this.lastSyncResult = null;
      try {
        const { data } = await syncPbxAccount(this.selectedAccount.id, payload);
        this.lastSyncResult = data;
        this.showSnackbar(this.$t("ncrm.integrations.pbx.snackbar.syncOk", data), "success");
        await this.loadAll();
      } catch (error) {
        this.syncError = this.extractError(error, "ncrm.integrations.pbx.snackbar.syncError");
        this.showSnackbar(this.syncError, "error");
        await this.loadAll();
      } finally {
        this.syncing = false;
      }
    },
    async fetchCalls() {
      this.callsLoading = true;
      try {
        const { data } = await fetchPbxCalls({ status: this.callStatusFilter || undefined, perPage: 50 });
        this.calls = Array.isArray(data?.calls) ? data.calls.map(normalizePbxCall) : [];
      } catch (error) {
        this.showSnackbar(this.extractError(error, "ncrm.integrations.pbx.snackbar.callsError"), "error");
      } finally {
        this.callsLoading = false;
      }
    },
    async handleCallStatusChange(status) {
      this.callStatusFilter = status;
      await this.fetchCalls();
    },
    openMatchedEntity({ type, id } = {}) {
      const routeByType = {
        company: "NcrmCompanyDetails",
        person: "NcrmPersonDetails",
        deal: "NcrmDealDetails",
      };
      const name = routeByType[String(type || "").toLowerCase()];
      if (!name || !(Number(id) > 0)) {
        this.showSnackbar(this.$t("ncrm.integrations.pbx.snackbar.openUnavailable"), "warning");
        return;
      }
      this.$router.push({ name, params: { id: Number(id) } });
    },
    async downloadRecording(call) {
      if (!call?.id || this.downloadingCallId) return;
      this.downloadingCallId = call.id;
      try {
        await triggerPbxRecordingDownload(call.id);
      } catch (error) {
        this.showSnackbar(
          isPbxRecordingUnavailableError(error)
            ? this.$t("ncrm.integrations.pbx.snackbar.recordingUnavailable")
            : this.extractError(error, "ncrm.integrations.pbx.snackbar.recordingError"),
          isPbxRecordingUnavailableError(error) ? "warning" : "error"
        );
      } finally {
        this.downloadingCallId = null;
      }
    },
    openRaw(call) {
      this.rawDrawer = { show: true, call };
    },
    openLink(call) {
      this.linkDrawer = { show: true, call, entityType: "company", entityId: null, matchedPhone: call.call_from_number || call.call_to_number || "", saving: false, error: "" };
    },
    async submitLink() {
      if (!this.linkDrawer.call) return;
      this.linkDrawer.error = "";
      this.linkDrawer.saving = true;
      try {
        await linkPbxCall(this.linkDrawer.call.id, {
          entity_type: this.linkDrawer.entityType,
          entity_id: this.linkDrawer.entityId,
          matched_phone: this.linkDrawer.matchedPhone,
        });
        this.linkDrawer.show = false;
        this.showSnackbar(this.$t("ncrm.integrations.pbx.snackbar.linked"), "success");
        await this.fetchCalls();
      } catch (error) {
        this.linkDrawer.error = this.extractError(error, "ncrm.integrations.pbx.snackbar.linkError");
      } finally {
        this.linkDrawer.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-pbx__title { margin: 0; font-size: 1.2rem; font-weight: 700; color: var(--ncrm-color-text-strong); }
.ncrm-pbx__subtitle { margin: 2px 0 0; color: rgba(var(--v-theme-on-surface), 0.62); }
.ncrm-pbx__panel { border: 1px solid var(--ncrm-color-border-default); border-radius: var(--app-radius-base); }
.ncrm-pbx__main-stack { display: grid; gap: 16px; }
.ncrm-pbx__accounts { display: grid; gap: 6px; }
.ncrm-pbx__account-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; width: 100%; padding: 8px 10px; border: 1px solid var(--ncrm-color-border-default); border-radius: var(--app-radius-base); background: rgb(var(--v-theme-surface)); cursor: pointer; text-align: left; }
.ncrm-pbx__account-row:hover,
.ncrm-pbx__account-row--active { border-color: var(--ncrm-color-primary-border); background: var(--ncrm-color-hover-soft); }
.ncrm-pbx__raw { white-space: pre-wrap; word-break: break-word; padding: 12px; border-radius: var(--app-radius-base); background: var(--ncrm-color-hover-soft); font-family: var(--app-font-family-mono); font-size: 0.82rem; }
</style>
